import { useCallback, useEffect, useRef, useState } from "react";
import { message, notification } from "antd";
import { baseUrl } from "../constantData";
import { IFileStatus } from "../interfaces/jobData";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveTab, selectBatchId, selectIsUploadModalOpen, setBatchId, setUploadCVsJobId } from "../../../modules/createJob/slice/jobSlice";
import { selectCurrentHiringStages, setFirstStageCount } from "../../../modules/createJob/slice/candidateSlice";
import NotificationDescription from "../../components/Modals/NotificationDescription";

const useBulkUploadStatus = () => {
    const dispatch = useDispatch();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [api, contextHolder] = notification.useNotification();
    const batchId = localStorage.getItem('batchId');
    const notificationKey = 'bulkUploadStatusNotification';
    const reduxBatchId = useSelector(selectBatchId);
    const activeTab = useSelector(selectActiveTab)
    const isNewUpload = useSelector(selectIsUploadModalOpen)
    const firstStageName = useSelector(selectCurrentHiringStages)[0]?.name
    const [status, setStatus] = useState<IFileStatus | null>(null);
    const [isUploaded, setIsUploaded] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setBatchId(localStorage.getItem('batchId')));
    }, [dispatch])

    const handleClose = useCallback(() => {
        api.destroy(notificationKey);
        localStorage.removeItem('fileCount');
        localStorage.removeItem("uploadNotificationType");
        localStorage.removeItem("notificationTimerStart");
        dispatch(setFirstStageCount(0))
        if(!isNewUpload){
            dispatch(setUploadCVsJobId(null))
        }
        setIsUploaded(false);
    }, [api, isNewUpload, dispatch])

    useEffect(() => {
        if (isNewUpload && isUploaded) {
            dispatch(setBatchId(null));
            localStorage.removeItem('batchId');
            handleClose();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        }
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isNewUpload, isUploaded, handleClose, dispatch]);
    
    useEffect(() => {
        if (isUploaded || (status?.status && status?.status !== 200)) {
            dispatch(setBatchId(null));
            localStorage.removeItem('batchId');
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            localStorage.setItem("notificationTimerStart", Date.now().toString());

            timeoutRef.current = setTimeout(() => {
                handleClose();
                localStorage.removeItem("notificationTimerStart");
            }, 30000);
        }
    }, [isUploaded, status?.status, handleClose, dispatch])

    useEffect(() => {
        const notifType = localStorage.getItem("uploadNotificationType");
        if (!notifType) return;

        const timerStart = localStorage.getItem("notificationTimerStart");
        if (!timerStart) return;

        const elapsed = Date.now() - Number(timerStart);
        
        if (elapsed >= 30000) {
            handleClose();
            localStorage.removeItem("notificationTimerStart");
            return;
        }

        const remaining = 30000 - elapsed;

        timeoutRef.current = setTimeout(() => {
            handleClose();
            localStorage.removeItem("notificationTimerStart");
        }, remaining);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [handleClose]);

    useEffect(() => {
        const notifType = localStorage.getItem("uploadNotificationType");

        if (notifType) {
            setIsUploaded(notifType === "success");

            api.open({
                key: notificationKey,
                message: '',
                description: (<NotificationDescription type={notifType === "success" ? "success" : "error"} status={status} onClose={handleClose} />),
                placement: 'bottomRight',
                duration: null,
                style: {
                    background: notifType === "error" ? '#c81e1e' : undefined,
                },
            });
        }
    }, [status, api, handleClose]);

    useEffect(() => {
        if (batchId === null) {
            return;
        }

        let eventSource: EventSource;

        try {
            eventSource = new EventSource(`${baseUrl}applications/bulkUploadStatus/${batchId}`, { withCredentials: true });

            eventSource.onmessage = (event) => {
                try {
                    const newStatus = JSON.parse(event?.data) as IFileStatus;
                    const isEqual = newStatus?.fileCount === newStatus?.totalFiles;
                    setIsUploaded(isEqual);
                    setStatus(newStatus);
                    if (isEqual || newStatus?.status !== 200) {
                        localStorage.setItem("uploadNotificationType", isEqual ? "success" : "error");
                    }
                    if (isEqual) {
                        eventSource.close();
                    }

                    if (newStatus?.fileCount) {
                        if (activeTab === firstStageName && Number(localStorage.getItem('fileCount')) !== newStatus.fileCount) {
                            localStorage.setItem('fileCount', newStatus.fileCount.toString());  // Update fileCount in localStorage
                            window.location.reload();
                            return;
                        }
                        dispatch(setFirstStageCount(newStatus.candidateStageApplicationCount))
                    }
                    
                    api.open({
                        key: notificationKey,
                        message: '',
                        description: (<NotificationDescription type={isEqual ? "success" : newStatus?.status !== 200 ? "error" : "progress"} status={newStatus} onClose={handleClose} />),
                        placement: 'bottomRight',
                        style: { background: `${(newStatus?.status !== 200 && !isEqual) && '#c81e1e'}` },
                        duration: null,
                    });
                } catch (e) {
                    console.error('Failed to parse event data:', e);
                    eventSource.close();
                    message.error('Something failed with the uploading')
                }
            };

            eventSource.onerror = (error) => {
                console.error('EventSource failed:', error);
                eventSource.close();
            };
        } catch (e) {
            console.error('Failed to initialize EventSource:', e);
            message.error('Something failed with the uploading')
            
        }
        
        return () => {
            if (eventSource) {
                eventSource.close();
            }
        };
        // eslint-disable-next-line
    }, [api, batchId, reduxBatchId, dispatch, handleClose, firstStageName, activeTab]);

    return { status, contextHolder };
};

export default useBulkUploadStatus;
